import React, { useState, useMemo } from 'react';
import { Link } from "react-router-dom";
import { Auth } from "../services/api";
import { FaEnvelope, FaSpinner, FaArrowLeft, FaTimes, FaCheck } from "react-icons/fa";
import logo from "../logo.svg";

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ForgotPassword = () => {
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [touched, setTouched] = useState(false);

  const emailValid = useMemo(() => EMAIL_REGEX.test(email), [email]);
  const emailError = useMemo(() => {
    if (email.length === 0) return "Email is required";
    if (!emailValid) return "Please enter a valid email address (e.g. name@company.com)";
    return "";
  }, [email, emailValid]);

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    if (!emailValid) return;
    setMessage('');
    setLoading(true);

    try {
      const res = await Auth.forgot({ email });
      setMessage(res.data.message || "A reset link has been sent to your email.");
    } catch (err) {
      setMessage("Something went wrong. Please check the email and try again.");
    } finally {
      setLoading(false);
    }
  };

  const fieldBorder = () => {
    if (!touched && email.length === 0) return "border-blue-400/30";
    return emailValid ? "border-green-400/60" : "border-red-400/60";
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-900 via-purple-900 to-gray-900 relative overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden z-0">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob"></div>
        <div className="absolute top-1/3 right-1/4 w-96 h-96 bg-purple-500 rounded-full mix-blend-multiply filter blur-3xl opacity-20 animate-blob animation-delay-2000"></div>
      </div>

      <div className="bg-white/10 backdrop-blur-lg rounded-2xl shadow-2xl border border-white/20 p-8 md:p-12 max-w-md w-full z-10 relative mx-4">
        <div className="text-center mb-8">
          <img src={logo} alt="Logo" className="h-12 mx-auto mb-4" />
          <h2 className="text-3xl font-bold text-white mb-2">Forgot Password?</h2>
          <p className="text-blue-200 text-sm">Enter your email to reset your password</p>
        </div>

        {message && (
          <div className="bg-blue-500/20 border border-blue-500/50 text-blue-100 px-4 py-3 rounded-lg mb-6 text-sm text-center">
            {message}
          </div>
        )}

        <form onSubmit={handleForgotPassword} className="space-y-6">
          <div>
            <label className="block text-blue-100 text-xs font-bold mb-2 uppercase tracking-wide">Email Address</label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <FaEnvelope className="text-blue-300" />
              </div>
              <input
                type="text"
                placeholder="name@company.com"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setTouched(true)}
                disabled={loading}
                className={`w-full pl-10 pr-4 py-3 bg-white/5 border ${fieldBorder()} rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-400 text-white placeholder-blue-300/50 transition-all duration-200`}
              />
            </div>
            {touched && emailError && (
              <p className="text-red-300 text-xs mt-1.5 flex items-start gap-1.5">
                <FaTimes className="mt-0.5 flex-shrink-0" size={10} />
                <span>{emailError}</span>
              </p>
            )}
            {touched && emailValid && (
              <p className="text-green-300 text-xs mt-1.5 flex items-center gap-1.5">
                <FaCheck size={10} /> Valid email address
              </p>
            )}
          </div>

          <button
            type="submit"
            disabled={loading || !emailValid}
            className={`w-full font-bold py-3 px-4 rounded-lg shadow-lg transform transition-all duration-200 ${
              emailValid && !loading
                ? "bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 text-white hover:scale-[1.02] active:scale-95 cursor-pointer"
                : "bg-gray-600/50 text-gray-400 cursor-not-allowed"
            }`}
          >
            {loading ? (
              <span className="flex items-center justify-center">
                <FaSpinner className="animate-spin mr-2" />
                Sending...
              </span>
            ) : (
              'Send Reset Link'
            )}
          </button>
        </form>

        <div className="mt-8 text-center">
          <Link to="/login" className="text-blue-200 hover:text-white text-sm font-medium flex items-center justify-center transition-colors">
            <FaArrowLeft className="mr-2" size={12} />
            Back to Login
          </Link>
        </div>
      </div>
    </div>
  );
};

export default ForgotPassword;
